import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { OrderEvent } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';

import { newestFirst } from '../schemas/order-rules';

/** Every state change of the order, newest first, with who did it and whether the shopper saw it. */
export function OrderTimeline({ events }: { events: readonly OrderEvent[] }) {
  const { t } = useTranslation('orders');
  const format = useFormat();
  const sorted = newestFirst(events);

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('timeline.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('timeline.empty')}</p>
        ) : (
          <ol
            className="relative flex flex-col gap-4 border-l pl-4"
            aria-label={t('timeline.title')}
          >
            {sorted.map((event) => (
              <li key={event.id} className="relative text-sm">
                <span
                  className="bg-border absolute top-1.5 -left-[21px] size-2.5 rounded-full ring-4 ring-[var(--card)]"
                  aria-hidden
                />
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium">
                    {t(`timeline.kind.${event.kind}`, { defaultValue: event.kind })}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {t(`timeline.actor.${event.actorType}`, { defaultValue: event.actorType })}
                  </span>
                  <span
                    className="text-muted-foreground text-xs"
                    title={format.utc(event.createdAt)}
                  >
                    {format.relative(event.createdAt)}
                  </span>
                  {event.visibleToCustomer ? (
                    <Badge variant="outline" className="text-muted-foreground gap-1 text-[11px]">
                      <Eye aria-hidden />
                      {t('timeline.visibleToCustomer')}
                    </Badge>
                  ) : null}
                </div>
                {event.message ? (
                  <p className="text-muted-foreground mt-0.5 whitespace-pre-line">
                    {event.message}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
